import { service } from '@ember/service';
import type { SyncHasMany } from '@ember-data/model';
import Model, { attr, hasMany } from '@ember-data/model';
import type AbilitiesService from 'ember-can/services/abilities';
import { sourceToAbilityMap } from 'garaje/abilities/entry-approval';
import type { GetCanReviewOrReadBlocklistMatchModel } from 'garaje/abilities/security';
import type { ReportSource } from 'garaje/models/approval-status';
import type { EntryWithVisitApproval } from 'garaje/models/entry';
import type { InviteWithVisitApproval } from 'garaje/models/invite';
import type ScreenerResult from 'garaje/models/screener-result';
import type { BlocklistReport, ReportWithReason, VisualComplianceReport } from 'garaje/models/screener-result';
import type ScreenerReview from 'garaje/models/screener-review';

export type ApprovalStatus = 'approved' | 'denied' | 'pending' | 'review';

class VisitApproval extends Model {
  @service declare abilities: AbilitiesService;

  @attr('string') declare prioritizedStatus: ApprovalStatus;
  @attr('boolean') declare employeeTriggeredRescreen: boolean;

  /**
   * Results from screeners. Will be at most 1 result per screener.
   */
  @hasMany('screener-result', { async: false, inverse: 'visitApproval' })
  declare screenerResults: SyncHasMany<ScreenerResult>;

  /**
   * Historical list of screener reviews. Latest reviews will be at the end by default. Can have more than 1 per screener.
   */
  @hasMany('screener-review', { async: false, inverse: 'visitApproval' })
  declare screenerReviews: SyncHasMany<ScreenerReview>;

  /**
   * all screener results that are in a review state.
   */
  get reviewResults(): ScreenerResult[] {
    return this.screenerResults.filter((result) => result.status === 'review');
  }

  /**
   * all screener results whose screening decision is 'fail'.
   */
  get failedResults(): ScreenerResult[] {
    return this.screenerResults.filter((result) => result.decision === 'fail');
  }

  /**
   * all screener results that are in a pending state.
   */
  get pendingResults(): ScreenerResult[] {
    return this.screenerResults.filter((result) => result.status === 'pending');
  }

  get blocklistResult(): ScreenerResult<BlocklistReport> | undefined {
    return this.screenerResults.find((result) => result.isBlocklist());
  }

  get visualComplianceResult(): ScreenerResult<VisualComplianceReport> | undefined {
    return this.screenerResults.find((result) => result.isVisualCompliance());
  }

  get blocklistReviewResult(): ScreenerResult<BlocklistReport> | undefined {
    return this.reviewResults.find((result) => result.isBlocklist());
  }

  get identityVerificationReviewResult(): ScreenerResult | undefined {
    return this.reviewResults.find((result) => result.isIdentityVerification());
  }

  get tempScreeningReviewResult(): ScreenerResult | undefined {
    return this.reviewResults.find((result) => result.isTempScreening());
  }

  get visitorDocumentsReviewResult(): ScreenerResult | undefined {
    return this.reviewResults.find((result) => result.isVisitorDocuments());
  }

  get visualComplianceReviewResult(): ScreenerResult<VisualComplianceReport> | undefined {
    return this.reviewResults.find((result) => result.isVisualCompliance());
  }

  get idScanningResult(): ScreenerResult | undefined {
    return this.screenerResults.find((result) => result.isIdScanning());
  }

  get idScanningReviewResult(): ScreenerResult | undefined {
    return this.reviewResults.find((result) => result.isIdScanning());
  }

  get idScanningPendingResult(): ScreenerResult | undefined {
    return this.pendingResults.find((result) => result.isIdScanning());
  }

  get preRegistrationRequiredResult(): ScreenerResult | undefined {
    return this.screenerResults.find((result) => result.isPreRegistrationRequired());
  }

  get preRegistrationRequiredReviewResult(): ScreenerResult | undefined {
    return this.reviewResults.find((result) => result.isPreRegistrationRequired());
  }

  get capacityScreeningReviewResult(): ScreenerResult | undefined {
    return this.reviewResults.find((result) => result.isCapacityScreening());
  }

  get outgoingInviteReviewResult(): ScreenerResult | undefined {
    return this.reviewResults.find((result) => result.isOutgoingInvite());
  }

  get conditionalRuleReviewResult(): ScreenerResult<ReportWithReason> | undefined {
    return this.reviewResults.find((result) => result.isConditionalRule());
  }

  /**
   * returns true if this visit approval cannot have all of its in review screener sources approved
   */
  get partiallyApprovable(): boolean {
    return this.reviewableScreenerSources.length !== this.reviewResults.length;
  }

  /**
   * returns a list of screener sources part of this visit approval that the current user can review
   */
  get reviewableScreenerSources(): ReportSource[] {
    const model: GetCanReviewOrReadBlocklistMatchModel = {
      context: 'location',
      visitApproval: this,
    };

    return this.reviewResults
      .map((result) => result.screenerSource)
      .filter((screenerSource) => {
        const source = sourceToAbilityMap[screenerSource] ?? screenerSource;
        return this.abilities.can(`review ${source}`, model);
      });
  }

  /**
   * returns a list of screener sources part of this visit approval that the current user cannot review
   */
  get unreviewableScreenerSources(): ReportSource[] {
    const model: GetCanReviewOrReadBlocklistMatchModel = {
      context: 'location',
      visitApproval: this,
    };

    return this.reviewResults
      .map((result) => result.screenerSource)
      .filter((screenerSource) => {
        const source = sourceToAbilityMap[screenerSource] ?? screenerSource;
        return !this.abilities.can(`review ${source}`, model);
      });
  }
}

export default VisitApproval;
export type GuestWithVisitApproval = InviteWithVisitApproval | EntryWithVisitApproval;

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    'visit-approval': VisitApproval;
  }
}
